import React from "react";

import {
  FaArrowLeft,
  FaBookOpen,
  FaImage,
  FaLayerGroup,
  FaListOl,
  FaPlus,
  FaQuestionCircle,
  FaSyncAlt,
} from "react-icons/fa";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  ToastContainer,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScrollToTop";

import useTutorialEditor from "../hooks/useTutorialEditor";

import TutorialStatusBadge from "../components/TutorialStatusBadge";
import TutorialSectionPanel from "../components/TutorialSectionPanel";
import TutorialFaqPanel from "../components/TutorialFaqPanel";

import TutorialSectionDrawer from "../components/TutorialSectionDrawer";
import TutorialStepDrawer from "../components/TutorialStepDrawer";
import TutorialMediaDrawer from "../components/TutorialMediaDrawer";
import TutorialFaqDrawer from "../components/TutorialFaqDrawer";

import ConfirmContentDialog from "../components/ConfirmContentDialog";

export default function TutorialEditorPage() {
  useScrollToTop();

  const navigate = useNavigate();

  const { tutorialId } =
    useParams();

  const {
    tutorial,
    sortedSections,
    sortedFaqs,
    statistics,

    loading,
    refreshing,
    error,
    actionLoading,

    expandedSections,
    toggleSectionExpanded,

    drawer,

    openCreateSection,
    openEditSection,

    openCreateStep,
    openEditStep,

    openCreateMedia,
    openEditMedia,

    openCreateFaq,
    openEditFaq,

    closeDrawer,
    saveDrawer,

    toggleSectionActive,
    toggleStepActive,
    toggleMediaActive,
    toggleFaqActive,

    moveSection,
    moveStep,

    confirmation,

    requestDeleteSection,
    requestDeleteStep,
    requestDeleteMedia,
    requestDeleteFaq,

    closeConfirmation,
    confirmDelete,

    loadTutorial,
  } = useTutorialEditor(
    tutorialId
  );

  if (loading) {
    return <EditorSkeleton />;
  }

  if (error || !tutorial) {
    return (
      <EditorError
        message={
          error ||
          "Tutoriel introuvable."
        }
        onBack={() =>
          navigate(
            "/appli/tutorials"
          )
        }
        onRetry={() =>
          loadTutorial()
        }
      />
    );
  }

  const busy =
    Boolean(actionLoading);

  return (
    <div className="space-y-8 p-4 lg:p-6">
      {/* ------------------------------------------------------------------ */}
      {/*                              EN-TÊTE                               */}
      {/* ------------------------------------------------------------------ */}

      <section className="relative overflow-hidden rounded-[30px] border border-slate-800 bg-slate-950 text-white shadow-[0_25px_70px_-25px_rgba(15,23,42,0.85)]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(99,102,241,0.40),transparent_32%),radial-gradient(circle_at_top_right,rgba(217,70,239,0.30),transparent_28%),radial-gradient(circle_at_bottom_left,rgba(16,185,129,0.15),transparent_30%)]" />

        <div className="relative p-6 md:p-8">
          <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
            <div className="max-w-3xl">
              <button
                type="button"
                onClick={() =>
                  navigate(
                    "/appli/tutorials"
                  )
                }
                className="inline-flex items-center gap-2 text-sm font-medium text-white/70 transition hover:text-white"
              >
                <FaArrowLeft />
                Retour aux tutoriels
              </button>

              <div className="mt-5 flex flex-wrap items-center gap-3">
                <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-white/75">
                  <FaBookOpen />
                  Éditeur de contenu
                </div>

                <TutorialStatusBadge
                  status={
                    tutorial.statut
                  }
                />
              </div>

              <h1 className="mt-4 text-3xl font-semibold tracking-tight md:text-4xl">
                {tutorial.titre}
              </h1>

              <p className="mt-3 max-w-2xl text-sm leading-6 text-white/70 md:text-base">
                {tutorial.sous_titre}
              </p>

              <div className="mt-4 flex flex-wrap gap-2 text-xs text-white/65">
                <span className="rounded-full bg-white/10 px-3 py-1">
                  {tutorial.app_key}
                </span>

                <span className="rounded-full bg-white/10 px-3 py-1">
                  Version{" "}
                  {tutorial.version}
                </span>

                <span className="rounded-full bg-white/10 px-3 py-1">
                  {tutorial.site_key ||
                    "Tous les sites"}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                disabled={busy}
                onClick={
                  openCreateSection
                }
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 transition hover:bg-slate-100 disabled:opacity-50"
              >
                <FaPlus />
                Ajouter une section
              </button>

              <button
                type="button"
                disabled={busy}
                onClick={
                  openCreateFaq
                }
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/15 bg-fuchsia-500/20 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-fuchsia-500/30 disabled:opacity-50"
              >
                <FaQuestionCircle />
                Ajouter une FAQ
              </button>

              <button
                type="button"
                disabled={refreshing}
                onClick={() =>
                  loadTutorial({
                    silent: true,
                  })
                }
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/15 bg-white/10 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-white/20 disabled:opacity-50"
              >
                <FaSyncAlt
                  className={
                    refreshing
                      ? "animate-spin"
                      : ""
                  }
                />

                Rafraîchir
              </button>
            </div>
          </div>

          <div className="mt-7 grid grid-cols-2 gap-4 lg:grid-cols-5">
            <HeroStat
              icon={<FaLayerGroup />}
              label="Sections"
              value={
                statistics.sections
              }
              detail={`${statistics.activeSections} active(s)`}
            />

            <HeroStat
              icon={<FaLayerGroup />}
              label="Sections actives"
              value={
                statistics.activeSections
              }
            />

            <HeroStat
              icon={<FaListOl />}
              label="Étapes"
              value={
                statistics.steps
              }
              detail={`${statistics.activeSteps} active(s)`}
            />

            <HeroStat
              icon={<FaImage />}
              label="Médias"
              value={
                statistics.medias
              }
              detail={`${statistics.activeMedias} actif(s)`}
            />

            <HeroStat
              icon={
                <FaQuestionCircle />
              }
              label="FAQ"
              value={
                statistics.faqs
              }
              detail={`${statistics.activeFaqs} active(s)`}
            />
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/*                        SECTIONS ET ÉTAPES                           */}
      {/* ------------------------------------------------------------------ */}

      <section>
        <div className="mb-5">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-violet-500">
            Structure du guide
          </div>

          <h2 className="mt-1 text-xl font-semibold text-slate-900 dark:text-slate-50">
            Sections et étapes
          </h2>

          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Chaque section représente
            une entrée du sommaire et
            contient plusieurs étapes
            ordonnées.
          </p>
        </div>

        {sortedSections.length ? (
          <div className="space-y-5">
            {sortedSections.map(
              (section, index) => (
                <TutorialSectionPanel
                  key={section.id}
                  section={section}
                  index={index}
                  total={
                    sortedSections.length
                  }
                  expanded={expandedSections.has(
                    section.id
                  )}
                  busy={busy}
                  onToggleExpanded={() =>
                    toggleSectionExpanded(
                      section.id
                    )
                  }
                  onCreateStep={() =>
                    openCreateStep(
                      section.id
                    )
                  }
                  onEditSection={() =>
                    openEditSection(
                      section
                    )
                  }
                  onDeleteSection={() =>
                    requestDeleteSection(
                      section
                    )
                  }
                  onToggleSectionActive={() =>
                    toggleSectionActive(
                      section
                    )
                  }
                  onMoveSectionUp={() =>
                    moveSection(
                      section.id,
                      "up"
                    )
                  }
                  onMoveSectionDown={() =>
                    moveSection(
                      section.id,
                      "down"
                    )
                  }
                  onEditStep={
                    openEditStep
                  }
                  onDeleteStep={
                    requestDeleteStep
                  }
                  onToggleStepActive={
                    toggleStepActive
                  }
                  onMoveStep={moveStep}
                  onCreateMedia={
                    openCreateMedia
                  }
                  onEditMedia={
                    openEditMedia
                  }
                  onDeleteMedia={
                    requestDeleteMedia
                  }
                  onToggleMediaActive={
                    toggleMediaActive
                  }
                />
              )
            )}
          </div>
        ) : (
          <EmptySectionsState
            onCreate={
              openCreateSection
            }
          />
        )}
      </section>

      {/* ------------------------------------------------------------------ */}
      {/*                                 FAQ                                */}
      {/* ------------------------------------------------------------------ */}

      <TutorialFaqPanel
        faqs={sortedFaqs}
        busy={busy}
        onCreate={openCreateFaq}
        onEdit={openEditFaq}
        onDelete={
          requestDeleteFaq
        }
        onToggleActive={
          toggleFaqActive
        }
      />

      {/* ------------------------------------------------------------------ */}
      {/*                              DRAWERS                               */}
      {/* ------------------------------------------------------------------ */}

      <TutorialSectionDrawer
        drawer={drawer}
        saving={
          actionLoading ===
          "save-section"
        }
        onClose={closeDrawer}
        onSave={saveDrawer}
      />

      <TutorialStepDrawer
        drawer={drawer}
        saving={
          actionLoading ===
          "save-step"
        }
        onClose={closeDrawer}
        onSave={saveDrawer}
      />

      <TutorialMediaDrawer
        drawer={drawer}
        saving={
          actionLoading ===
          "save-media"
        }
        onClose={closeDrawer}
        onSave={saveDrawer}
      />

      <TutorialFaqDrawer
        drawer={drawer}
        saving={
          actionLoading ===
          "save-faq"
        }
        onClose={closeDrawer}
        onSave={saveDrawer}
      />

      {/* ------------------------------------------------------------------ */}
      {/*                           CONFIRMATION                              */}
      {/* ------------------------------------------------------------------ */}

      <ConfirmContentDialog
        confirmation={
          confirmation
        }
        loading={Boolean(
          actionLoading?.startsWith(
            "delete-"
          )
        )}
        onClose={
          closeConfirmation
        }
        onConfirm={confirmDelete}
      />

      <ToastContainer />
    </div>
  );
}

function HeroStat({
  icon,
  label,
  value,
  detail,
}) {
  return (
    <div className="rounded-[22px] border border-white/15 bg-white/10 p-4 backdrop-blur">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.16em] text-white/60">
            {label}
          </div>

          <div className="mt-2 text-2xl font-semibold">
            {value}
          </div>

          {detail ? (
            <div className="mt-1 text-[11px] text-white/50">
              {detail}
            </div>
          ) : null}
        </div>

        <div className="shrink-0 rounded-xl bg-white/10 p-2.5">
          {icon}
        </div>
      </div>
    </div>
  );
}

function EmptySectionsState({
  onCreate,
}) {
  return (
    <div className="rounded-[28px] border border-dashed border-slate-300 bg-white p-10 text-center dark:border-slate-700 dark:bg-slate-900">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[22px] bg-violet-100 text-2xl text-violet-600 dark:bg-violet-500/15 dark:text-violet-300">
        <FaLayerGroup />
      </div>

      <h3 className="mt-5 text-xl font-semibold text-slate-900 dark:text-slate-50">
        Aucune section
      </h3>

      <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
        Commence par ajouter une section
        au tutoriel.
      </p>

      <button
        type="button"
        onClick={onCreate}
        className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-700"
      >
        <FaPlus />
        Ajouter une section
      </button>
    </div>
  );
}

function EditorSkeleton() {
  return (
    <div className="space-y-6 p-4 lg:p-6">
      <div className="h-[390px] animate-pulse rounded-[30px] bg-slate-800" />

      {Array.from({
        length: 4,
      }).map((_, index) => (
        <div
          key={index}
          className="h-40 animate-pulse rounded-[26px] bg-slate-200 dark:bg-slate-800"
        />
      ))}
    </div>
  );
}

function EditorError({
  message,
  onBack,
  onRetry,
}) {
  return (
    <div className="flex min-h-[70vh] items-center justify-center p-4">
      <div className="w-full max-w-lg rounded-[28px] border border-rose-200 bg-white p-8 text-center shadow-xl dark:border-rose-900/40 dark:bg-slate-900">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50">
          Chargement impossible
        </h2>

        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          {message}
        </p>

        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="rounded-2xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 dark:border-slate-700 dark:text-slate-200"
          >
            Retour
          </button>

          <button
            type="button"
            onClick={onRetry}
            className="rounded-2xl bg-violet-600 px-4 py-2.5 text-sm font-medium text-white"
          >
            Réessayer
          </button>
        </div>
      </div>
    </div>
  );
}